import { Suspense, useCallback, useEffect, useState, type ReactNode } from "react";
import { Button, Drawer, Dropdown, Grid, Layout, Menu, Segmented, Space, Spin, Tabs, Tooltip, Typography, theme, type MenuProps } from "antd";
import { CloseOutlined, LogoutOutlined, MenuOutlined } from "@ant-design/icons";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { auth, getUserInfo } from "../shared/auth.js";
import { OpenTabCtx, TabParamsCtx, type OpenTab } from "./tabs/context.js";
import { VIEWS, DEFAULT_VIEW, MENU } from "./tabs/registry.js";
import { FONT_SIZES, type Mode } from "./theme.js";
import { VersionFooter } from "./VersionFooter.js";

const { Header, Sider, Content } = Layout;
// Router basename = the Vite `base` (set via VITE_BASE_PATH at build time, default /giftcard/admin2/),
// trailing slash stripped so URL math matches. Lets the same source deploy under /admin, /giftcard/admin2, etc.
const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type Tab = { key: string; view: string; title: string; path: string };
const parse = (path: string) => { const [view, qs = ""] = path.split("?"); return { view, params: new URLSearchParams(qs) }; };
const titleFor = (path: string) => { const { view, params } = parse(path); return VIEWS[view]?.title(params) ?? view; };

/** Wraps one antd tab node so it can be dragged (dnd-kit sortable). The whole tab is the drag handle;
 *  a small drag distance is required (via the sensor) so a plain click still selects/closes the tab. */
function SortableTab({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: "grab",
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1 : undefined,
  };
  return <div ref={setNodeRef} style={style} {...attributes} {...listeners}>{children}</div>;
}

function readUrlPath(): string {
  const p = location.pathname.startsWith(BASE) ? location.pathname.slice(BASE.length) : location.pathname;
  const view = p.replace(/^\//, "").split("/")[0] || DEFAULT_VIEW;
  if (!VIEWS[view]) return DEFAULT_VIEW;
  const qs = location.search.replace(/^\?/, "");
  return qs ? `${view}?${qs}` : view;
}

/** Multi-tab workspace: keep-alive tabs (Ant keeps inactive panes mounted → state preserved),
 *  URL synced to the active tab (deep-link), open tabs restored from localStorage, cross-link via openTab. */
export function Workspace({ mode, setMode, fontSize, setFontSize }: {
  mode: Mode; setMode: (m: Mode) => void; fontSize: number; setFontSize: (n: number) => void;
}) {
  const [tabs, setTabs] = useState<Tab[]>(() => {
    const init = readUrlPath();
    return [{ key: init, view: parse(init).view, title: titleFor(init), path: init }];
  });
  const [active, setActive] = useState<string>(() => tabs[0]!.key);
  const [who, setWho] = useState<string>("");
  const { token } = theme.useToken();
  // Mobile (< lg): the side menu becomes a hamburger-triggered Drawer. Desktop (lg+) is unchanged.
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.lg;
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { getUserInfo().then((u) => setWho(u.name || u.email || "Admin")); }, []);

  // restore previously-open tabs (merge with the URL tab, which stays active)
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("giftme2.tabs") || "null") as { tabs: Tab[] } | null;
      if (saved?.tabs?.length) setTabs((cur) => {
        const map = new Map(saved.tabs.filter((t) => VIEWS[t.view]).map((t) => [t.key, t] as const));
        for (const t of cur) map.set(t.key, t);
        return [...map.values()];
      });
    } catch { /* ignore */ }
  }, []);

  // persist tabs + sync the browser URL to the active tab
  useEffect(() => {
    localStorage.setItem("giftme2.tabs", JSON.stringify({ tabs }));
    const t = tabs.find((x) => x.key === active);
    if (t) history.replaceState(null, "", `${BASE}/${t.path}`);
  }, [tabs, active]);

  const openTab = useCallback<OpenTab>((path, title) => {
    const { view } = parse(path);
    if (!VIEWS[view]) return;
    setTabs((cur) => (cur.some((t) => t.key === path) ? cur : [...cur, { key: path, view, title: title ?? titleFor(path), path }]));
    setActive(path);
  }, []);

  const dashboardTab = (): Tab => ({ key: DEFAULT_VIEW, view: DEFAULT_VIEW, title: titleFor(DEFAULT_VIEW), path: DEFAULT_VIEW });

  const removeTab = (key: string) => setTabs((cur) => {
    const idx = cur.findIndex((t) => t.key === key);
    const next = cur.filter((t) => t.key !== key);
    if (next.length === 0) { setActive(DEFAULT_VIEW); return [dashboardTab()]; }
    if (key === active) setActive((next[idx] ?? next[next.length - 1])!.key);
    return next;
  });

  // Shared close: drop every tab in `kill`, then keep the two invariants — never empty (→ fresh
  // Dashboard), and keep a valid active tab (`keepActive` when the caller pins one; else, if the active
  // tab was closed, fall back to the last survivor). `active` is read from state, not the updater, so
  // the setActive call stays outside setTabs (no state-update-during-render).
  const closeTabs = (kill: Set<string>, keepActive?: string) => {
    if (kill.size === 0) return;
    const next = tabs.filter((t) => !kill.has(t.key));
    if (next.length === 0) { setActive(DEFAULT_VIEW); setTabs([dashboardTab()]); return; }
    if (keepActive) setActive(keepActive);
    else if (kill.has(active)) setActive(next[next.length - 1]!.key);
    setTabs(next);
  };

  // Close ALL → land on a fresh Dashboard (your requested default).
  const closeAll = () => { setActive(DEFAULT_VIEW); setTabs([dashboardTab()]); };
  // Close every tab EXCEPT `key` → it stays and becomes active (standard "close others").
  const closeOthers = (key: string) => closeTabs(new Set(tabs.filter((t) => t.key !== key).map((t) => t.key)), key);
  // Close tabs to the RIGHT / LEFT of `key` (by position in the strip); the clicked tab stays active.
  const idxOf = (key: string) => tabs.findIndex((t) => t.key === key);
  const closeToRight = (key: string) => { const i = idxOf(key); if (i >= 0) closeTabs(new Set(tabs.slice(i + 1).map((t) => t.key)), key); };
  const closeToLeft = (key: string) => { const i = idxOf(key); if (i >= 0) closeTabs(new Set(tabs.slice(0, i).map((t) => t.key)), key); };

  // Drag-to-reorder. A small activation distance means a plain click still selects/closes a tab —
  // only an actual drag starts the sort. The reordered array flows into the existing persist effect,
  // so the new order survives a reload (localStorage "giftme2.tabs") for free.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setTabs((cur) => {
      const from = cur.findIndex((t) => t.key === active.id);
      const to = cur.findIndex((t) => t.key === over.id);
      return from < 0 || to < 0 ? cur : arrayMove(cur, from, to);
    });
  };

  // Right-click menu for a tab (VS Code / Postman style). Directional items disable when there's
  // nothing that way, so the menu never shows dead options.
  const tabMenu = (key: string): MenuProps => {
    const i = idxOf(key);
    const only = tabs.length <= 1;
    return {
      items: [
        { key: "close", label: "Close" },
        { key: "others", label: "Close others", disabled: only },
        { key: "right", label: "Close tabs to the right", disabled: i < 0 || i >= tabs.length - 1 },
        { key: "left", label: "Close tabs to the left", disabled: i <= 0 },
        { type: "divider" },
        { key: "all", label: "Close all", danger: true },
      ],
      onClick: ({ key: action, domEvent }) => {
        domEvent.stopPropagation();
        if (action === "close") removeTab(key);
        else if (action === "others") closeOthers(key);
        else if (action === "right") closeToRight(key);
        else if (action === "left") closeToLeft(key);
        else if (action === "all") closeAll();
      },
    };
  };

  const nav = (
    <>
      <div style={{ padding: 16, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 64 }}>
        <img src={`${import.meta.env.BASE_URL}mybuy-mark.png`} alt=""
          style={{ width: 34, height: 34, borderRadius: 9, objectFit: "cover" }} />
        <span style={{ marginInlineStart: 10, fontSize: 20, fontWeight: 800, letterSpacing: -0.4, whiteSpace: "nowrap" }}>
          <span style={{ color: mode === "night" ? "#FFFFFF" : "#0F1F17" }}>My</span>
          {/* A DARKENED version of the app icon's lime (Palette.brandLime #C1DE63). The icon's own lime is
              measured at 1.51:1 on this white sidebar — invisible. This one is 3.33:1 on white and 5.53:1 on
              the night sidebar, so the one fixed hex clears 3:1 (large text) in both modes. admin2 cannot
              read the Flutter palette, so the value is duplicated here deliberately. */}
          <span style={{ color: "#6E9A18" }}>Buy</span>
        </span>
      </div>
      <Menu mode="inline" selectedKeys={[parse(active).view]} items={MENU}
        onClick={(e) => { if (e.key.startsWith("grp-")) return; openTab(e.key); if (isMobile) setDrawerOpen(false); }} style={{ borderInlineEnd: "none" }} />
    </>
  );

  return (
    <OpenTabCtx.Provider value={openTab}>
      <Layout style={{ height: "100vh", overflow: "hidden" }}>
        {!isMobile && (
          <Sider theme="light" collapsible width={248} style={{ borderInlineEnd: `1px solid ${token.colorBorderSecondary}`, height: "100vh", overflow: "auto" }}>
            {/* Nav grows; the version tag is pinned to the bottom (marginTop:auto) of a full-height column. */}
            <div style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
              {nav}
              <VersionFooter />
            </div>
          </Sider>
        )}
        {isMobile && (
          <Drawer placement="left" open={drawerOpen} onClose={() => setDrawerOpen(false)} width={280} closable={false} styles={{ body: { padding: 0 } }}>
            {nav}
          </Drawer>
        )}
        <Layout>
          <Header style={{ background: token.colorBgContainer, borderBottom: `1px solid ${token.colorBorderSecondary}`, height: 52, lineHeight: "52px", display: "flex", justifyContent: "space-between", alignItems: "center", paddingInline: 16 }}>
            {isMobile
              ? <Button type="text" icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} aria-label="Menu" />
              : <Typography.Text type="secondary">Hello, {who}</Typography.Text>}
            <Space>
              <Segmented size="small" value={fontSize} onChange={(v) => setFontSize(Number(v))}
                options={FONT_SIZES.map((s, i) => ({ value: s, label: ["A-", "A", "A+"][i] }))} />
              <Segmented size="small" value={mode} onChange={(v) => setMode(v as Mode)}
                options={[{ value: "day", label: "☀" }, { value: "night", label: "🌙" }, { value: "sepia", label: "▦" }]} />
              <Tooltip title="Sign out"><Button icon={<LogoutOutlined />} onClick={() => { auth.clear(); location.reload(); }} /></Tooltip>
            </Space>
          </Header>
          <Content style={{ padding: isMobile ? "8px 8px 12px" : "12px 16px 16px", overflow: "auto" }}>
            <Tabs type="editable-card" hideAdd activeKey={active} onChange={setActive}
              tabBarStyle={{ marginBottom: 12 }}
              onEdit={(k, a) => { if (a === "remove") removeTab(k as string); }}
              // Drag-to-reorder: wrap antd's own tab bar so each tab node is a dnd-kit sortable item.
              // DefaultTabBar still renders the real tabs (close ×, active state, overflow) — we only
              // wrap each node, so no antd behaviour is lost.
              renderTabBar={(tabBarProps, DefaultTabBar) => (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  <SortableContext items={tabs.map((t) => t.key)} strategy={horizontalListSortingStrategy}>
                    <DefaultTabBar {...tabBarProps}>
                      {(node) => <SortableTab key={node.key} id={String(node.key)}>{node}</SortableTab>}
                    </DefaultTabBar>
                  </SortableContext>
                </DndContext>
              )}
              tabBarExtraContent={{
                right: (
                  <Tooltip title="Close all tabs">
                    <Button size="small" type="text" icon={<CloseOutlined />}
                      disabled={tabs.length <= 1 && tabs[0]?.key === DEFAULT_VIEW}
                      onClick={closeAll}>Close all</Button>
                  </Tooltip>
                ),
              }}
              items={tabs.map((t) => ({
                key: t.key,
                // Right-click the tab label for the VS Code / Postman close menu.
                label: (
                  <Dropdown menu={tabMenu(t.key)} trigger={["contextMenu"]}>
                    <span>{t.title}</span>
                  </Dropdown>
                ),
                children: (
                  <TabParamsCtx.Provider value={parse(t.path).params}>
                    <Suspense fallback={<div style={{ padding: 48, textAlign: "center" }}><Spin /></div>}>
                      {VIEWS[t.view]!.element}
                    </Suspense>
                  </TabParamsCtx.Provider>
                ),
              }))} />
          </Content>
        </Layout>
      </Layout>
    </OpenTabCtx.Provider>
  );
}
